import { useEffect, useMemo } from 'react';
import { Modal, Form, Input, Button, Divider } from 'antd';
import PermissionPicker from './PermissionPicker';
import type { RoleGroup, RoleGroupFormValues } from './types';

interface Props {
    open: boolean;
    mode: 'add' | 'edit';
    initialValue?: RoleGroup | null;
    loading?: boolean;
    onCancel: () => void;
    onSubmit: (values: RoleGroupFormValues) => void;
}

const emptyValues: RoleGroupFormValues = {
    name: '',
    description: '',
    permissions: [],
};

export default function PermissionGroupModal({
    open,
    mode,
    initialValue,
    loading,
    onCancel,
    onSubmit,
}: Props) {
    const [form] = Form.useForm<RoleGroupFormValues>();

    const defaults = useMemo<RoleGroupFormValues>(() => {
        if (mode === 'edit' && initialValue) {
            return {
                name: initialValue.name,
                description: initialValue.description ?? '',
                permissions: initialValue.permissions ?? [],
            };
        }
        return emptyValues;
    }, [mode, initialValue]);

    useEffect(() => {
        if (open) form.setFieldsValue(defaults);
    }, [open, defaults, form]);

    const handleClose = () => {
        form.resetFields();
        onCancel();
    };

    const handleFinish = (values: RoleGroupFormValues) => {
        onSubmit({
            ...values,
            description: values.description?.trim() || '',
            permissions: values.permissions ?? [],
        });
    };

    return (
        <Modal
            title={mode === 'add' ? 'Thêm nhóm quyền' : `Sửa nhóm quyền: ${initialValue?.name ?? ''}`}
            open={open}
            onCancel={handleClose}
            footer={null}
            destroyOnClose
            width={900}
            centered
            maskClosable={false}
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={defaults}
                onFinish={handleFinish}
            >
                <div className="grid grid-cols-1 gap-x-4">
                    <Form.Item
                        label="Tên nhóm quyền"
                        name="name"
                        rules={[{ required: true, message: 'Vui lòng nhập tên nhóm quyền' }]}
                    >
                        <Input placeholder="VD: Trưởng phòng kinh doanh" />
                    </Form.Item>
                </div>

                <Form.Item label="Mô tả" name="description">
                    <Input.TextArea
                        rows={2}
                        placeholder="Mô tả ngắn về mục đích của nhóm quyền này"
                        maxLength={255}
                        showCount
                    />
                </Form.Item>

                <Divider className="my-3" orientation="left" orientationMargin={0}>
                    <span className="text-sm font-semibold">Phân quyền theo module</span>
                </Divider>

                <Form.Item
                    name="permissions"
                    rules={[
                        {
                            validator: (_, v: string[]) =>
                                v && v.length > 0
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Vui lòng chọn ít nhất 1 quyền')),
                        },
                    ]}
                >
                    <PermissionPickerField />
                </Form.Item>

                <div className="flex justify-end gap-2 pt-2 border-t">
                    <Button onClick={handleClose}>Hủy</Button>
                    <Button type="primary" htmlType="submit" loading={loading}
                        className="!bg-[#3e91ab] hover:!bg-[#357f96]">
                        {mode === 'add' ? 'Tạo nhóm quyền' : 'Lưu thay đổi'}
                    </Button>
                </div>
            </Form>
        </Modal>
    );
}

/**
 * Wrapper để PermissionPicker dùng được với antd Form.Item
 * (Form sẽ tự inject value + onChange).
 */
function PermissionPickerField({
    value,
    onChange,
}: {
    value?: string[];
    onChange?: (next: string[]) => void;
}) {
    return (
        <PermissionPicker
            value={value ?? []}
            onChange={(next) => onChange?.(next)}
        />
    );
}
